import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown, Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShineBorder } from '@/components/ui/shine-border';
import DetailedPricingTable from './pricing/DetailedPricingTable';
import SubscriptionModel from './pricing/SubscriptionModel';
import VendorModel from './pricing/VendorModel';

type PricingModel = 'subscription' | 'vendor';

interface VariantTableProps {
  defaultModel?: PricingModel;
  showPricingGrid?: boolean;
}


const VariantTable: React.FC<VariantTableProps> = ({ defaultModel = 'subscription', showPricingGrid = false }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [selectedModel, setSelectedModel] = useState<PricingModel>(defaultModel);
  const [basicBundle, setBasicBundle] = useState("75");
  const [standardBundle, setStandardBundle] = useState("150");

  const getPrice = (bundle: string, isStandard: boolean) => {
    if (isStandard) {
      switch (bundle) {
        case "75":
          return "29,970";
        case "150":
          return "54,970";
        case "250":
          return "84,970";
        default:
          return "29,970";
      }
    } else {
      switch (bundle) {
        case "75":
          return "19,970";
        case "150":
          return "34,970";
        case "250":
          return "54,970";
        default:
          return "19,970";
      }
    }
  };
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('opacity-100');
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const toggleView = () => {
    setShowDetailedView(!showDetailedView);
  };
  
  const handleModelChange = (model: PricingModel) => {
    setSelectedModel(model);
  };
  return <section id="plans" ref={sectionRef} className="py-20 bg-white opacity-0 transition-opacity duration-1000" style={{
    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0, 0, 0, 0.05) 1px, transparent 0)',
    backgroundSize: '40px 40px'
  }}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-block px-3 py-1 bg-brand-blue/10 rounded-full mb-3">
            <span className="text-sm font-medium text-brand-blue">Pricing</span>
          </div>
          {showPricingGrid ? (
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-brand-darkgrey mb-6">
                Still paying for resume databases and job postings?
              </h1>
            </>
          ) : (
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-brand-darkgrey mb-4">One platform. 2 ways to hire.</h1>
              <h2 className="text-lg text-brand-grey">Same shortlist engine. Use it yourself or let our team handle everything</h2>
            </>
          )}
        </div>

        {!showDetailedView ? (
          <>
            {showPricingGrid ? (
              <>
                {/* Cutshort Benefits List */}
                <div className="max-w-5xl mx-auto mb-8">
                  <ul className="space-y-3 flex flex-col items-center">
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl font-bold mt-0.5">✓</span>
                      <span className="text-brand-grey text-lg">No licenses or seats required</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl font-bold mt-0.5">✓</span>
                      <span className="text-brand-grey text-lg">Pay only for candidates you like</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl font-bold mt-0.5">✓</span>
                      <span className="text-brand-grey text-lg">Spend 70% time on closing right candidates</span>
                    </li>
                  </ul>
                </div>

                {/* Pricing Grid - 2 Options */}
                <div className="max-w-5xl mx-auto mb-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    {/* Basic Plan */}
                    <div className="bg-white rounded-3xl p-10 flex flex-col items-center border border-blue-200">
                      {/* Plan Badge */}
                      <div className="bg-brand-blue text-white px-6 py-2.5 rounded-full text-lg font-semibold mb-6">
                        Basic pack
                      </div>

                      {/* Bundle Selector */}
                      <div className="w-full mb-6">
                        <Select value={basicBundle} onValueChange={setBasicBundle}>
                          <SelectTrigger className="w-full bg-white border border-cs-light-grey rounded-xl h-12 text-base text-cs-dark-grey">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="75">75 candidates bundle (approx 3 offers)</SelectItem>
                            <SelectItem value="150">150 candidates bundle (approx 6 offers)</SelectItem>
                            <SelectItem value="250">250 candidates bundle (approx 10 offers)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Features */}
                      <div className="space-y-3 w-full mb-8">
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Pack validity: 3 months</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Talent database access</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Recruiter seats: No limit</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Candidate salary limit: 35LPA</span>
                        </div>
                      </div>

                      {/* Get Started Button */}
                      <a
                        href="https://cutshort.io/a/generic-emp-form"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-brand-blue text-white px-8 py-3.5 rounded-full text-base font-semibold hover:opacity-90 transition-opacity w-full text-center"
                      >
                        Buy for ₹{getPrice(basicBundle, false)}
                      </a>
                    </div>

                    {/* Standard Plan */}
                    <div className="bg-white rounded-3xl p-10 flex flex-col items-center border-2 border-brand-blue">
                      {/* Plan Badge */}
                      <div className="bg-cs-accent-yellow text-cs-dark-grey px-6 py-2.5 rounded-full text-lg font-semibold mb-6">
                        Standard pack
                      </div>

                      {/* Bundle Selector */}
                      <div className="w-full mb-6">
                        <Select value={standardBundle} onValueChange={setStandardBundle}>
                          <SelectTrigger className="w-full bg-white border border-cs-light-grey rounded-xl h-12 text-base text-cs-dark-grey">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="75">75 candidates bundle (approx 3 offers)</SelectItem>
                            <SelectItem value="150">150 candidates bundle (approx 6 offers)</SelectItem>
                            <SelectItem value="250">250 candidates bundle (approx 10 offers)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Features */}
                      <div className="space-y-3 w-full mb-8">
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Pack validity: 3 months</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Talent database access</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Recruiter seats: No limit</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0 text-brand-blue" />
                          <span className="text-sm text-cs-dark-grey">Candidate salary limit: No limit</span>
                        </div>
                      </div>

                      {/* Get Started Button */}
                      <a
                        href="https://cutshort.io/a/generic-emp-form"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-brand-blue text-white px-8 py-3.5 rounded-full text-base font-semibold hover:opacity-90 transition-opacity w-full text-center"
                      >
                        Buy for ₹{getPrice(standardBundle, true)}
                      </a>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Radio Button Selection */}
                <div className="max-w-2xl mx-auto mb-8">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                    <label className="flex items-center gap-3 cursor-pointer p-4 bg-white rounded-lg border-2 transition-all hover:border-brand-blue/50" style={{ borderColor: selectedModel === 'subscription' ? 'rgb(59, 130, 246)' : 'rgb(229, 231, 235)' }}>
                      <input
                        type="radio"
                        name="pricing-model"
                        value="subscription"
                        checked={selectedModel === 'subscription'}
                        onChange={() => handleModelChange('subscription')}
                        className="w-5 h-5 text-brand-blue focus:ring-brand-blue"
                      />
                      <span className="text-lg font-medium text-brand-darkgrey">Hire yourself</span>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer p-4 bg-white rounded-lg border-2 transition-all hover:border-brand-blue/50" style={{ borderColor: selectedModel === 'vendor' ? 'rgb(59, 130, 246)' : 'rgb(229, 231, 235)' }}>
                      <input
                        type="radio"
                        name="pricing-model"
                        value="vendor"
                        checked={selectedModel === 'vendor'}
                        onChange={() => handleModelChange('vendor')}
                        className="w-5 h-5 text-brand-blue focus:ring-brand-blue"
                      />
                      <span className="text-lg font-medium text-brand-darkgrey">We do it for you</span>
                    </label>
                  </div>
                  <p className="text-center text-brand-grey text-md font-medium">
                    {selectedModel === 'subscription' 
                      ? "Use our platform to generate your own shortlist and close them" 
                      : "Tell us what you need, we deliver candidates & work with you to onboard them"}
                  </p>
                </div>

                {/* Content based on selection */}
                <div className="mb-12 transition-all duration-300">
                  {selectedModel === 'subscription' ? (
                    <SubscriptionModel />
                  ) : (
                    <VendorModel />
                  )}
                </div>

                {/* CTA Section */}
                <div className="text-center max-w-2xl mx-auto">
                  <p className="text-brand-grey mb-6">
                    To understand how much you can save with Cutshort, book a call now.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <a href="https://cutshort.io/a/generic-emp-form" target="_blank" rel="noopener noreferrer" className="py-3 px-6 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-blue/90 transition-colors shadow-md text-center">
                      Get a call
                    </a>
                    {selectedModel === 'vendor' && (
                      <button onClick={toggleView} className="py-3 px-6 border border-brand-blue text-brand-blue rounded-lg font-medium hover:bg-brand-blue/5 transition-colors text-center flex items-center justify-center gap-2">
                        See detailed comparison
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <DetailedPricingTable onToggleView={toggleView} />
        )}
      </div>
    </section>;
};
export default VariantTable;